const headerItemLevel0Modifier = {
  fontSize: "1.125rem",
  lineHeight: 1.1111,
  fontWeight: "medium",
  color: "primary.midGrey",
  bgColor: "transparent",
  h: "100%",
  px: { base: 5, xl: 6 },
  py: 5,
  _hover: {
    textDecoration: "none",
    bgColor: "grey.lighter",
    color: "primary.midGrey",
  },
}

const headerItemLevel1Modifier = {
  fontSize: "0.875rem",
  lineHeight: 1.7143,
  textAlign: "center",
  fontWeight: "medium",
  whiteSpace: "normal",
  color: "primary.midGrey",
  h: "auto",
  borderRadius: "25px",
  py: 1.5,
  px: 3,
  display: "block",
  _after: {
    content: `" "`,
    d: "block",
    borderBottom: "2px solid",
    borderBottomColor: "primary.midGrey",
    transition: "width 0.3s ease",
    w: 0,
  },
  _hover: {
    textDecoration: "none",
    _after: {
      w: "full",
    },
  },
  _active: {
    py: 1.5,
    px: 3,
  },
}

const buttonFontStylings = {
  // rebrand-graphik: buttons now use Graphik Medium (was Larsseit 400)
  fontFamily: "graphik",
  fontFeatureSettings: '"ss01" on, "ss02" on',
  fontWeight: "medium",
  lineHeight: 1.5,
  textTransform: "capitalize",
}

const buttonGeneralStylings = {
  width: "auto",
  textDecoration: "unset",
  borderRadius: "14px",
  px: 6,
  py: 2.5,
}

export const buttonLoadingState = {
  backgroundColor: "primary.white",
  borderColor: "border.lightest",
  color: "secondary.grey",
  fill: "secondary.grey",
}

const buttonSelectedState = {
  // pressed state uses a neutral outline (was secondary.midPink) per PDP testing feedback
  borderColor: "border.lightest",
  color: "text.primary",
  fill: "text.primary",
}

export const buttonDisabledState = {
  backgroundColor: "grey.lighter",
  borderColor: "grey.lighter",
  color: "secondary.grey",
  fill: "secondary.grey",
}

export const carouselArrowDisabledState = {
  backgroundColor: "grey.lightest",
  borderColor: "border.lightest",
  color: "border.lighter",
}

const tertiaryDisabledState = {
  backgroundColor: "grey.lighter",
  color: "secondary.grey",
  fill: "secondary.grey",
  borderColor: "transparent",
}

const iconDisabledState = {
  background: "unset",
  backgroundColor: "secondary.grey",
  color: "primary.white",
  fill: "secondary.grey",
  borderColor: "transparent",
}

const tertiaryLoadingState = {
  backgroundColor: "grey.lighter",
  color: "primary.midGrey",
  fill: "grey.lighter",
  borderColor: "primary.midGrey",
}

const buttonFocusState = {
  // neutral focus ring (was system.focus blue) per PDP testing feedback on Notify Me
  outline: "2px solid",
  outlineColor: "primary.midGrey",
  outlineOffset: "2px",
  boxShadow: "none",
}

const primaryVariant = {
  backgroundColor: "primary.corePink",
  color: "text.primary",
  fill: "text.primary",
  border: "2px solid",
  borderColor: "primary.corePink",
  textTransform: "uppercase",
  _hover: {
    backgroundColor: "secondary.midPink",
    color: "text.primary",
    fill: "text.primary",
    borderColor: "secondary.midPink",
    textDecoration: "unset",
    _disabled: buttonDisabledState,
    _loading: buttonLoadingState,
  },
  _active: {
    ...buttonSelectedState,
    backgroundColor: "primary.white",
    _disabled: buttonDisabledState,
    _loading: buttonLoadingState,
  },
  _selected: {
    ...buttonSelectedState,
    backgroundColor: "primary.white",
    _disabled: buttonDisabledState,
    _loading: buttonLoadingState,
  },
  _disabled: buttonDisabledState,
  _loading: buttonLoadingState,
  _focusVisible: buttonFocusState,
}

export const Button = {
  baseStyle: {
    ...buttonFontStylings,
    ...buttonGeneralStylings,
    _disabled: {
      opacity: 1,
      pointerEvents: "none",
    },
    _loading: buttonLoadingState,
  },
  sizes: {
    large: {
      h: "48px",
      py: 0,
      fontSize: "1rem",
      lineHeight: 1.375,
    },
    small: {
      fontSize: "0.875rem",
      fontWeight: "medium",
      lineHeight: 1.4286,
    },
  },
  variants: {
    primary: primaryVariant,
    secondary: {
      backgroundColor: "primary.white",
      color: "text.primary",
      fill: "text.primary",
      border: "2px solid",
      borderColor: "border.lightest",
      textTransform: "uppercase",
      _hover: {
        backgroundColor: "primary.corePink",
        color: "text.primary",
        fill: "text.primary",
        borderColor: "primary.corePink",
        textDecoration: "unset",
        _disabled: buttonDisabledState,
        _loading: buttonLoadingState,
      },
      _active: {
        ...buttonSelectedState,
        backgroundColor: "primary.corePink",
        _disabled: buttonDisabledState,
        _loading: buttonLoadingState,
      },
      _selected: {
        ...buttonSelectedState,
        backgroundColor: "primary.corePink",
        _disabled: buttonDisabledState,
        _loading: buttonLoadingState,
      },
      _disabled: buttonDisabledState,
      _loading: buttonLoadingState,
      _focusVisible: buttonFocusState,
    },
    tertiary: {
      backgroundColor: "primary.white",
      color: "primary.midGrey",
      fill: "primary.midGrey",
      border: "1.5px solid",
      borderColor: "transparent",
      _hover: {
        backgroundColor: "primary.midGrey",
        color: "primary.white",
        fill: "primary.white",
        borderColor: "transparent",
        _disabled: tertiaryDisabledState,
        _loading: tertiaryLoadingState,
      },
      _active: {
        backgroundColor: "grey.light",
        color: "primary.midGrey",
        fill: "primary.midGrey",
        borderColor: "primary.midGrey",
        _disabled: tertiaryDisabledState,
        _loading: tertiaryLoadingState,
      },
      _disabled: tertiaryDisabledState,
      _loading: tertiaryLoadingState,
      _focus: {
        borderColor: "transparent",
        outlineStyle: "solid",
        outlineWidth: "2px",
        outlineColor: "preset.blue",
      },
    },
    text: {
      fontWeight: "normal",
      color: "primary.midGrey",
      fill: "primary.midGrey",
      textDecoration: "underline",
      textUnderlineOffset: "5px",
      textDecorationThickness: "1px",
      backgroundColor: "transparent",
      borderRadius: 0,
      h: "unset",
      px: 0,
      py: 2.5,
      _disabled: {
        color: "secondary.grey",
      },
      _hover: {
        textDecorationThickness: "1px",
      },
      _focus: {
        outlineWidth: "2px",
        outlineColor: "preset.blue",
      },
    },
    icon: {
      backgroundColor: "primary.white",
      color: "primary.midGrey",
      fill: "primary.midGrey",
      border: "1.5px solid",
      borderColor: "primary.midGrey",
      textTransform: "initial",
      h: "unset",
      py: 2.5,
      _hover: {
        background: "primary.midGrey",
        color: "primary.white",
        fill: "primary.white",
        borderColor: "primary.midGrey",
        _disabled: iconDisabledState,
        _loading: buttonLoadingState,
      },
      _active: {
        background: "unset",
        backgroundColor: "primary.white",
        color: "inherit",
        fill: "inherit",
        borderColor: "primary.midGrey",
        _disabled: iconDisabledState,
        _loading: buttonLoadingState,
      },
      _disabled: iconDisabledState,
      _loading: buttonLoadingState,
      _focus: {
        borderColor: "transparent",
        outlineStyle: "solid",
        outlineWidth: "2px",
        outlineColor: "preset.blue",
      },
    },
    product: primaryVariant,
    // Special varints for specific <Button> components
    headerItemLevel0: {
      borderRadius: 0,
      ...headerItemLevel0Modifier,
      _hover: {
        ...headerItemLevel0Modifier._hover,
      },
    },
    headerItemLevel1: {
      ...headerItemLevel1Modifier,
      boxShadow: "none",
      _hover: {
        ...headerItemLevel1Modifier._hover,
      },
    },
    headerItemLevel1Active: {
      // ...headerItemLevel1Modifier,
      h: "unset",
      boxShadow: "none",
      color: "primary.midGrey",
    },
    headerMegaItemLevel2: {
      fontSize: "1.5rem",
      lineHeight: 1.6667,
      fontWeight: "bold",
      h: "unset",
      padding: 0,
      textDecoration: "none",
      _hover: {
        textDecoration: "none",
      },
    },
    mobileHeaderItemImage: {
      h: "auto",
      pl: 6,
      pr: "0",
      py: 3,
    },
    mobileSubmenuItem: {
      h: "auto",
      pl: 6,
      pr: "0",
      py: 1,
    },
    mobileSubmenuItemImage: {
      h: "auto",
      pl: 6,
      pr: "0",
      py: 1,
    },
    headerIcon: {
      color: "preset.blue",
      minWidth: 0,
      h: "unset",
      py: 2.5,
      transition: "all .4s ease-in-out",
      borderRadius: "4px",
      _focusVisible: {
        outline: "solid 1px",
        outlineColor: "preset.blue",
        bgColor: "grey.lighter",
        boxShadow: "0px 0px 5px 3px #7DC2FF95 !important",
      },
      _hover: {
        color: "text.disabled",
      },
      _active: {
        outline: "none",
        bgColor: "none",
        boxShadow: "none",
      },
    },
    cart: {
      h: "unset",
      minH: "unset",
      minW: "unset",
      w: "unset",
      lineHeight: 0,
      p: 2,
      color: "primary.midGrey",
      _disabled: {
        color: "text.disabled",
        opacity: 1,
      },
    },
    onlyIcon: {
      border: "unset",
      backgroundColor: "transparent",
      color: "primary.midGrey",
      fill: "primary.midGrey",
      h: "unset",
      p: 0,
      _disabled: {
        color: "secondary.grey",
        fill: "secondary.grey",
      },
    },
    count: {
      width: { base: "100%", sm: "40px" },
      backgroundColor: "unset",
      color: "primary.midGrey",
      fill: "primary.midGrey",
      p: 0,
    },
    wishlistShare: {
      display: "flex",
      gap: 2,
      h: "unset",
      p: 4,
      m: 0,
      w: "full",
      alignItems: "center",
      justifyContent: "flex-start",
      _hover: {
        backgroundColor: "grey.lightest",
      },
    },
    transparent: {
      minWidth: 0,
      h: "unset",
      p: 0,
    },
    giftWrapping: {
      cursor: "pointer",
      bg: "primary.white",
      borderRadius: "10px",
      borderWidth: "1px",
      borderColor: "#E2DAF8",
      h: "unset",
      py: 2.5,
      _active: {
        outline: "2px solid #C2A0F8 !important",
        outlineOffset: "2px",
      },
      _disabled: {
        opacity: "0.7!important",
      },
    },
  },
  defaultProps: {
    size: "large",
    variant: "transparent",
  },
}
